/*
 * HomeCrest EMI section - empty-state guard
 * ===========================================================================
 * The EMI plans drawer this file used to open is gone. HomeCrest no longer has
 * plans of its own to show: the official Cashfree BNPL Plus widget renders the
 * offers, and its own full-screen overlay is the "view plans" destination. So
 * there is no dialog left to open and no trigger left to listen for.
 *
 * WHAT IS LEFT, AND WHY
 * ---------------------
 * One job: do not leave a HomeCrest frame around nothing.
 *
 * Cashfree hides its OWN iframe - `style.display = 'none'` on the iframe
 * element - when the merchant has no offers for the amount, when the amount is
 * below its floor, or when its config is incomplete. Without this file the
 * HomeCrest panel would keep its heading, hairlines and small print wrapped
 * around an invisible box.
 *
 * HOW IT READS THAT, AND WHAT IT NEVER TOUCHES
 * --------------------------------------------
 * It reads the iframe ELEMENT's own style - a node in OUR document, placed
 * there by Cashfree's SDK. It never reads, and cannot read, anything inside the
 * frame: that is a cross-origin document and is Cashfree's alone.
 *
 * It also never calls a Cashfree endpoint, never derives an instalment, and
 * never inspects offer data. It only answers one question: did Cashfree render
 * something, yes or no.
 *
 * FAIL CLOSED. The panel starts hidden and is revealed only once a visible
 * Cashfree frame has actually been observed. If the SDK is blocked, fails, or
 * never runs, the section stays hidden rather than framing an empty box.
 *
 * THE OBSERVER IS NEVER TORN DOWN. An earlier version disconnected it after a
 * settle timeout, which left the panel stranded on screen if Cashfree switched
 * its frame off later than that - exactly the empty frame this file exists to
 * prevent. Cashfree removes and recreates the iframe on its own schedule, and
 * a variant change re-renders the section, so the only correct lifetime for
 * this watcher is the life of the page. It is one attribute-filtered observer
 * over one small subtree; leaving it running costs nothing measurable.
 *
 * NO window.onmessage. Cashfree's SDK ASSIGNS `window.onmessage` to drive its
 * overlay; assigning it here too would silently replace their handler and break
 * "view plans". This file adds no message listener at all - and if one is ever
 * needed it must use addEventListener('message', ...), never assignment.
 */

const PANEL = '[data-hc-emi-panel]';

/**
 * Is this frame actually showing something?
 *
 * `display` is read from the element's own computed style. An iframe Cashfree
 * has switched off reports `none`; one it is using reports otherwise. A frame
 * that does not exist yet is not showing anything either.
 *
 * @param {Element | null} frame
 * @returns {boolean}
 */
function isVisible(frame) {
  if (!frame) return false;
  return getComputedStyle(frame).display !== 'none';
}

/**
 * Watch one panel and mirror Cashfree's own visibility onto it, for as long as
 * the page lives.
 *
 * @param {HTMLElement} panel
 */
function guard(panel) {
  /* In the theme editor the merchant has to be able to see and select the
     section even before the widget resolves, so it is left alone entirely. */
  if (panel.hasAttribute('data-hc-design-mode')) return;

  panel.hidden = true;

  const sync = () => {
    /* Cashfree removes and recreates its iframe rather than reusing it, so the
       node is looked up fresh every time instead of being held onto. */
    panel.hidden = !isVisible(panel.querySelector('#cf-iframe'));
  };

  /* childList catches the iframe being created, swapped or removed; attributes
     catches the SDK switching an existing one off after its fetch resolves. */
  new MutationObserver(sync).observe(panel, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  });

  sync();
}

document.querySelectorAll(PANEL).forEach((panel) => {
  if (panel instanceof HTMLElement) guard(panel);
});
